import * as React from 'react';
import { useForm, type DefaultValues, type FieldValues, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, AlertDescription, AlertTitle } from '../../primitives/alert.js';
import { Button } from '../../primitives/button.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../primitives/card.js';
import { Form } from '../../primitives/form.js';
import { PageHeader } from '../../primitives/page-header.js';
import { Spinner } from '../../primitives/spinner.js';
import { cn } from '../../lib/cn.js';
import type { ResourceFormSpec } from '../types.js';
import { KitFormField } from './field.js';
import { applyServerErrors, fieldNamesOf } from './server-errors.js';
import { emitKitTelemetry, errorFieldNames } from '../telemetry.js';

export interface ResourceFormProps<TValues extends FieldValues> {
  /**
   * Names this form to the usage statistics (the resource's key). Without one
   * the form reports nothing.
   */
  formKey?: string;
  /** The form renders values; mapping them to a request body is the caller's job. */
  /** `never` for the item: the form only renders values, it never reads an item itself. */
  spec: ResourceFormSpec<TValues, unknown, never>;
  /** Undefined creates; a value edits. */
  initialValues?: TValues;
  onSubmit: (values: TValues) => Promise<unknown>;
  onCancel?: () => void;
  submitLabel?: string;
  title?: string;
  description?: string;
  /** Rendered next to the submit button — a delete action, usually. */
  actions?: React.ReactNode;
  className?: string;
}

/**
 * The kit's create/edit form.
 *
 * Client and server validate the same rules by construction: the resolver runs
 * the contract's own Zod schema, and a 422 from the server is written back onto
 * the same fields through `applyServerErrors` (ADR-0014).
 */
/** The leaves of react-hook-form's dirty-fields tree, as dotted paths. */
function flattenDirty(node: unknown, path = ''): Record<string, true> {
  if (node === true) return { [path]: true };
  if (node === null || typeof node !== 'object') return {};
  return Object.entries(node).reduce<Record<string, true>>(
    (all, [key, child]) => ({ ...all, ...flattenDirty(child, path ? `${path}.${key}` : key) }),
    {},
  );
}

/**
 * The values as the request will carry them: object keys whose value is
 * `undefined` dropped, at any depth, as `JSON.stringify` drops them.
 *
 * A control bound inside an object (`attributes.garden`) registers its key on
 * mount, holding `undefined` until somebody answers it. The wire never sees
 * that key, so neither should the schema: a record that refuses `undefined`
 * would otherwise block the save over a question nobody was obliged to answer.
 */
function withoutUndefined(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(withoutUndefined);
  // Plain objects only: a Date or a File is a value, not a bag of keys.
  if (value === null || typeof value !== 'object') return value;
  const prototype: unknown = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) return value;
  return Object.fromEntries(
    Object.entries(value)
      .filter(([, child]) => child !== undefined)
      .map(([key, child]) => [key, withoutUndefined(child)]),
  );
}

/** The named subset of the form's values, for the visibility conditions. */
function pick(values: Record<string, unknown>, keys: readonly string[]): Record<string, unknown> {
  return Object.fromEntries(keys.map((key) => [key, values[key]]));
}

export function ResourceForm<TValues extends FieldValues>({
  formKey,
  spec,
  initialValues,
  onSubmit,
  onCancel,
  submitLabel,
  title,
  description,
  actions,
  className,
}: ResourceFormProps<TValues>) {
  const [formError, setFormError] = React.useState<string | null>(null);

  const resolver = React.useMemo<Resolver<TValues>>(() => {
    const validate = zodResolver(spec.schema) as Resolver<TValues>;
    return (values, context, options) =>
      validate(withoutUndefined(values) as TValues, context, options);
  }, [spec.schema]);

  const form = useForm<TValues>({
    resolver,
    defaultValues: (initialValues ?? spec.defaultValues) as DefaultValues<TValues>,
    mode: 'onBlur',
  });

  const fieldNames = React.useMemo(() => fieldNamesOf(spec.sections), [spec.sections]);

  // The fields some other field's visibility depends on, and nothing else: the
  // form re-renders when one of those changes, not on every keystroke.
  const conditionKeys = React.useMemo(
    () =>
      [
        ...new Set(
          spec.sections.flatMap((section) =>
            section.fields.flatMap((field) => (field.visibleWhen ? [field.visibleWhen.field] : [])),
          ),
        ),
      ].sort(),
    [spec.sections],
  );

  const [conditions, setConditions] = React.useState<Record<string, unknown>>(() =>
    pick(form.getValues(), conditionKeys),
  );

  React.useEffect(() => {
    if (conditionKeys.length === 0) return;
    const subscription = form.watch((values) => {
      const next = pick(values, conditionKeys);
      setConditions((current) =>
        conditionKeys.every((key) => current[key] === next[key]) ? current : next,
      );
    });
    return () => subscription.unsubscribe();
  }, [form, conditionKeys]);

  const isVisible = React.useCallback(
    (field: { visibleWhen?: { field: string; equals: readonly unknown[] } }) =>
      !field.visibleWhen || field.visibleWhen.equals.includes(conditions[field.visibleWhen.field]),
    [conditions],
  );

  // Editing a record that arrives after first paint (the detail query resolves
  // late) must repopulate the form without clobbering what the user typed.
  React.useEffect(() => {
    if (initialValues && !form.formState.isDirty) form.reset(initialValues);
  }, [initialValues, form]);

  // Leaving with unsaved changes is a real loss on a long property form.
  React.useEffect(() => {
    if (!form.formState.isDirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [form.formState.isDirty]);

  // Usage statistics (M17, section E): opened, refused, submitted, abandoned.
  // Refs, not state: none of this may re-render the form.
  const openedAt = React.useRef(Date.now());
  const settled = React.useRef(false);
  const cancelled = React.useRef(false);
  const touched = React.useRef(0);
  touched.current = Object.keys(flattenDirty(form.formState.dirtyFields)).length;

  React.useEffect(() => {
    if (!formKey) return;
    settled.current = false;
    openedAt.current = Date.now();
    emitKitTelemetry({ type: 'form-opened', form: formKey });

    // Leaving is a change of route (unmount) or the page going away (hidden):
    // whichever comes first reports, once.
    const abandon = (): void => {
      if (settled.current) return;
      settled.current = true;
      emitKitTelemetry({
        type: 'form-abandoned',
        form: formKey,
        reason: cancelled.current ? 'cancelled' : 'left',
        touched: touched.current,
        seconds: Math.round((Date.now() - openedAt.current) / 1000),
      });
    };
    const onHidden = (): void => {
      if (document.visibilityState === 'hidden') abandon();
    };
    document.addEventListener('visibilitychange', onHidden);
    return () => {
      document.removeEventListener('visibilitychange', onHidden);
      abandon();
    };
  }, [formKey]);

  const submit = form.handleSubmit(
    async (values) => {
      setFormError(null);
      try {
        await onSubmit(values);
        form.reset(values);
        if (formKey) {
          settled.current = true;
          emitKitTelemetry({ type: 'form-submitted', form: formKey });
        }
      } catch (error) {
        const result = applyServerErrors(error, form.setError, fieldNames);
        setFormError(result.message);
      }
    },
    (errors) => {
      if (!formKey) return;
      for (const field of errorFieldNames(errors)) {
        emitKitTelemetry({ type: 'form-invalid', form: formKey, field });
      }
    },
  );

  const submitting = form.formState.isSubmitting;

  return (
    <Form {...form}>
      <form
        data-slot="resource-form"
        noValidate
        onSubmit={(event) => void submit(event)}
        onKeyDown={(event) => {
          // Ctrl/Cmd+Enter submits from anywhere in the form.
          if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') void submit();
        }}
        className={cn('flex flex-col gap-[var(--card-gap)]', className)}
      >
        {title && <PageHeader title={title} description={description} />}

        {formError && (
          <Alert tone="danger">
            <AlertTitle>Salvataggio non riuscito</AlertTitle>
            <AlertDescription className="whitespace-pre-line">{formError}</AlertDescription>
          </Alert>
        )}

        {spec.sections.map((section) => {
          const fields = section.fields.filter(isVisible);
          // A section whose every field is hidden is an empty card, which reads
          // as a bug rather than as a section that does not apply.
          if (!section.render && fields.length === 0) return null;

          return (
            <Card key={section.id}>
              {(section.title ?? section.description) && (
                <CardHeader>
                  {section.title && <CardTitle>{section.title}</CardTitle>}
                  {section.description && <CardDescription>{section.description}</CardDescription>}
                </CardHeader>
              )}
              <CardContent className="grid gap-[var(--card-gap)]">
                {section.renderBefore?.()}
                {section.render ? (
                  section.render()
                ) : (
                  <div
                    data-columns={section.columns ?? 2}
                    className={cn(
                      'grid gap-4',
                      (section.columns ?? 2) === 2 && 'sm:grid-cols-2',
                      section.columns === 3 && 'sm:grid-cols-2 lg:grid-cols-3',
                    )}
                  >
                    {fields.map((field) => (
                      <KitFormField key={field.name} field={field} disabled={submitting} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}

        <footer className="flex items-center gap-2">
          {actions}
          <div className="ml-auto flex items-center gap-2">
            {onCancel && (
              <Button
                type="button"
                variant="ghost"
                disabled={submitting}
                onClick={() => {
                  cancelled.current = true;
                  onCancel();
                }}
              >
                Annulla
              </Button>
            )}
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting && <Spinner className="text-current" />}
              {submitLabel ?? 'Salva'}
            </Button>
          </div>
        </footer>
      </form>
    </Form>
  );
}
